import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService } from './storage/local-storage.service';
export interface ConsentSettings {
  analytics_storage?: 'granted' | 'denied';
  ad_storage?: 'granted' | 'denied';
  ad_user_data?: 'granted' | 'denied';
  ad_personalization?: 'granted' | 'denied';
}

const CONSENT_STORAGE_KEY = 'user_consent_preferences';

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private currentConsent = new BehaviorSubject<ConsentSettings | null>(null);
  public currentConsent$ = this.currentConsent.asObservable();

  private initialDefaultConsent: ConsentSettings = {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };

  constructor(private localStorageService: LocalStorageService) {
    this.loadConsentFromStorage();
  }

  private loadConsentFromStorage(): void {
    const savedConsentString =
      this.localStorageService.getItem(CONSENT_STORAGE_KEY);
    if (savedConsentString) {
      const savedConsent: ConsentSettings = JSON.parse(savedConsentString);
      this.currentConsent.next(savedConsent);
      // Update gtag with stored preferences immediately on load
      this.updateGtagConsent(savedConsent, false); // false: not a new user decision, just applying stored
    } else {
      // No saved consent, currentConsent remains null initially
      // Default consent is already set in index.html
      // The UI component will prompt the user.
      this.currentConsent.next(this.initialDefaultConsent); // Reflect default state
    }
  }

  public saveConsent(settings: ConsentSettings): void {
    const newConsentState = { ...this.currentConsent.value, ...settings };
    this.localStorageService.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify(newConsentState)
    );
    this.currentConsent.next(newConsentState);
    this.updateGtagConsent(settings, true); // true: it's an active user decision
  }

  public updateGtagConsent(
    settings: ConsentSettings,
    isUserAction: boolean
  ): void {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', settings);
      // if (isUserAction) {
      // Optional: Fire an event if you want to track consent updates in GA
      // window.gtag('event', 'consent_update', { ... });
      // }
    } else {
      console.warn('gtag function not found. Consent update skipped.');
    }
  }

  public setDefaultConsentIfNeeded(): void {
    // This is mostly handled by index.html now.
    // This could be used if managing defaults entirely from Angular (e.g. conditional based on region).
    if (
      typeof window.gtag === 'function' &&
      !this.localStorageService.getItem(CONSENT_STORAGE_KEY)
    ) {
      console.log(
        'Setting default gtag consent from service (should be redundant if in index.html)'
      );
      window.gtag('consent', 'default', this.initialDefaultConsent);
    }
  }

  public getConsent(): ConsentSettings | null {
    return this.currentConsent.value;
  }

  public hasMadeChoice(): boolean {
    return !!this.localStorageService.getItem(CONSENT_STORAGE_KEY);
  }

  // Helper to get specific consent status
  public isGranted(type: keyof ConsentSettings): boolean {
    return this.currentConsent.value?.[type] === 'granted';
  }
}
